import ArrowRight20 from '@carbon/icons-react/es/arrow--right/20';
import React, { FC } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import styled from 'styled-components';
import { IconShell } from 'components/atoms/icon-shell';
import { InputBorder, InputFocus } from 'components/atoms/input/input';
import {
  Label01,
  ProductiveHeading07,
  Text01,
  Text02,
} from 'components/styles';

interface CardProps {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  footerDescription?: React.ReactNode;
  hoverable?: boolean;
}

export type CardLinkProps = CardProps & { link: LinkProps };

const Wrapper = styled.div<Pick<CardProps, 'hoverable'>>`
  padding: var(--spacing-05);
  min-height: 184px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  background-color: var(--white-100);
  box-shadow: var(--shadow-02);
  transition: box-shadow 0.3s, border-color 0.3s, -webkit-box-shadow 0.3s;

  &:hover {
    box-shadow: ${({ hoverable }) =>
      hoverable ? 'var(--shadow-16)' : 'var(--shadow-02)'};
  }
`;
const IconWrapper = styled.div`
  margin-bottom: var(--layout-01);
`;

const Content = styled.div`
  text-align: left;
`;

const CTA = styled.div`
  margin-left: auto;
  ${Text01};
`;

const CardTitle = styled.div`
  ${ProductiveHeading07};
  ${Text01};
  margin-bottom: var(--spacing-02);
`;

const CardDescription = styled.div`
  ${Text02};
`;

const FooterDescription = styled.div`
  ${Label01};
  ${Text01};
`;

const Footer = styled.div`
  display: flex;

  ${CTA} {
    display: inline-flex;
    align-self: flex-end;
    margin-left: auto;
  }
`;
const StyledLink = styled(Link)<LinkProps>`
  ${Wrapper} {
    ${InputBorder};
    border: none;
  }

  &:focus {
    ${Wrapper} {
      ${InputFocus};
    }
  }
`;

const Card: FC<CardProps> = ({
  icon,
  title,
  description,
  footerDescription,
  hoverable,
}) => (
  <Wrapper hoverable={hoverable}>
    <Content>
      {icon != null ? (
        <IconWrapper>
          <IconShell icon={icon} />
        </IconWrapper>
      ) : null}
      {title ? <CardTitle>{title}</CardTitle> : null}
      {description ? <CardDescription>{description}</CardDescription> : null}
    </Content>
    <Footer>
      {footerDescription ? (
        <FooterDescription>{footerDescription}</FooterDescription>
      ) : null}
      <CTA>
        <ArrowRight20 />
      </CTA>
    </Footer>
  </Wrapper>
);

export const CardLink: FC<CardLinkProps> = ({
  link: linkProps,
  ...cardProps
}) => {
  return (
    <StyledLink {...linkProps}>
      <Card {...cardProps} />
    </StyledLink>
  );
};
